import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { CurrencyDollarIcon, UserIcon, BuildingOfficeIcon, InformationCircleIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import PageHeader from '../../components/PageHeader';
import { getLocalApiUrl, authFetch } from '../../config/api';
import { useAttestationVersion } from '../../hooks/useAttestationVersion';
import {
  TIERS,
  payoutPolicyFromVenueRow,
  resolveTierFromAmount,
  formatTierRange,
  formatCurrency
} from './payoutPolicyUtils';

const PAYMENT_METHODS = ['Cash', 'Bank Transfer', 'Cheque', 'CRT', 'Other'];

const cashLabel = (v) => (v ? String(v).replaceAll('_', ' ') : '—');

export default function PayoutCapture() {
  const [venues, setVenues] = useState([]);
  const [tierLocked, setTierLocked] = useState(false);
  const [form, setForm] = useState({
    member_id: '',
    member_name: '',
    venue: '',
    amount: '',
    payout_tier: 'PV1',
    occupation: '',
    payment_method: 'Cash',
    game_type: 'Slot Machine',
    game_id: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  // S4 — resolve the active cashier + patron attestation wording so the
  // version shown at capture time is stamped on the payout record.
  const cashierAttestation = useAttestationVersion('cashier_payout_confirm');
  const patronAttestation = useAttestationVersion('patron_payout_receipt');
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await authFetch(getLocalApiUrl('/api/venues'));
        const j = await r.json();
        if (j.success && j.data) {
          setVenues(
            j.data.map((v) => ({
              ...v,
              policy: payoutPolicyFromVenueRow(v)
            }))
          );
        }
      } catch (_) {}
    })();
  }, []);

  const selectedVenue = useMemo(
    () => venues.find((v) => String(v.venue_id || v.id) === String(form.venue)),
    [venues, form.venue]
  );

  const tierPolicy = selectedVenue?.policy?.[form.payout_tier];

  /** Auto-set PV tier from amount using venue payout policy (same as Payout Rules Builder). */
  useEffect(() => {
    if (!selectedVenue?.policy || tierLocked) return;
    const amt = parseFloat(form.amount);
    const next = Number.isNaN(amt) || amt < 0
      ? 'PV1'
      : resolveTierFromAmount(amt, selectedVenue.policy);
    setForm((p) => (p.payout_tier === next ? p : { ...p, payout_tier: next }));
  }, [selectedVenue, form.amount, tierLocked]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    const amount = parseFloat(form.amount);
    if (!form.venue || Number.isNaN(amount) || amount <= 0) {
      setError('Venue and amount are required');
      return;
    }
    if (selectedVenue?.policy) {
      const tp = selectedVenue.policy[form.payout_tier];
      if (tp && !tp.enabled) {
        setError(`${form.payout_tier} is disabled for this venue in the payout policy.`);
        return;
      }
      if (tp?.occupationRequired && !String(form.occupation || '').trim()) {
        setError(`Occupation is required for ${form.payout_tier} at this venue (payout policy).`);
        return;
      }
    }
    const venueObj = selectedVenue;
    const venueName = venueObj?.name || form.venue;
    setSubmitting(true);
    try {
      const r = await authFetch(getLocalApiUrl('/api/monitoring/payouts'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          member_id: form.member_id || null,
          member_name: form.member_name || null,
          venue: venueName,
          amount,
          payout_tier: form.payout_tier,
          occupation: form.occupation || null,
          payment_method: form.payment_method,
          game_type: form.game_type || null,
          game_id: form.game_id || null,
          // S4: stamp the attestation wording the cashier is agreeing to by
          // submitting. Patron attestation is stamped only when the submitter
          // is also acknowledging on the patron's behalf (rare on this web
          // capture; mobile is the primary signing surface).
          attestation_version_ids: {
            ...(cashierAttestation.data?.id ? { cashier_confirm: cashierAttestation.data.id } : {}),
            ...(acknowledged && patronAttestation.data?.id ? { patron_receipt: patronAttestation.data.id } : {}),
          }
        })
      });
      const j = await r.json();
      if (!j.success) throw new Error(j.error || 'Failed to create payout');
      setSuccess(`Payout ${j.data?.id} recorded`);
      setForm((prev) => ({ ...prev, amount: '', occupation: '', notes: '' }));
      setTierLocked(false);
    } catch (err) {
      setError(err.message || 'Failed to record payout');
    } finally {
      setSubmitting(false);
    }
  };

  const onVenueChange = useCallback((e) => {
    setTierLocked(false);
    setForm((p) => ({ ...p, venue: e.target.value }));
  }, []);

  const onAmountChange = useCallback((e) => {
    setTierLocked(false);
    setForm((p) => ({ ...p, amount: e.target.value }));
  }, []);

  const onTierChange = useCallback((e) => {
    setTierLocked(true);
    setForm((p) => ({ ...p, payout_tier: e.target.value }));
  }, []);

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <PageHeader title="Payout Capture" subtitle="Uses each venue’s payout policy (PV1–PV4) from Venue payouts / Payout Rules Builder." />
      <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
        {error && <div className="p-3 bg-red-50 text-red-700 rounded text-sm">{error}</div>}
        {success && <div className="p-3 bg-green-50 text-green-700 rounded text-sm">{success}</div>}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Venue *</label>
          <select
            required
            value={form.venue}
            onChange={onVenueChange}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
          >
            <option value="">— Select Venue —</option>
            {venues.map((v) => (
              <option key={v.id} value={v.venue_id || v.id}>
                {v.name} ({v.code})
              </option>
            ))}
          </select>
        </div>

        {selectedVenue && tierPolicy && (
          <div className="rounded-lg border border-teal-100 bg-teal-50/80 p-4 text-sm text-gray-800">
            <div className="flex items-start gap-2 font-medium text-teal-900">
              <InformationCircleIcon className="h-5 w-5 shrink-0 text-teal-600" />
              <span>Payout policy for {form.payout_tier}</span>
            </div>
            <ul className="mt-2 space-y-1 text-xs text-gray-700 list-disc list-inside">
              <li>
                Amount band:{' '}
                {formatTierRange(tierPolicy.minAmount ?? 0, tierPolicy.maxAmount)}
                {!tierPolicy.enabled && <span className="text-amber-700 font-medium"> — tier disabled in policy</span>}
              </li>
              <li>KYC: {tierPolicy.kycRequired ? 'Required' : 'Not required'}</li>
              <li>Occupation: {tierPolicy.occupationRequired ? 'Required' : 'Optional'}</li>
              <li>Cash method: {cashLabel(tierPolicy.cashDispensed)}</li>
              <li>Signatures: {tierPolicy.signaturesRequired ?? 0}</li>
            </ul>
            {tierLocked && (
              <p className="mt-2 text-xs text-amber-800">
                Tier was set manually; change amount or venue to auto-match tier from policy again.
              </p>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Member ID</label>
            <input
              type="text"
              value={form.member_id}
              onChange={(e) => setForm((p) => ({ ...p, member_id: e.target.value }))}
              placeholder="e.g. M12345"
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Member Name</label>
            <input
              type="text"
              value={form.member_name}
              onChange={(e) => setForm((p) => ({ ...p, member_name: e.target.value }))}
              placeholder="Optional"
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($) *</label>
            <input
              type="number"
              required
              min="0"
              step="0.01"
              value={form.amount}
              onChange={onAmountChange}
              placeholder="0.00"
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
            {selectedVenue && form.amount !== '' && !Number.isNaN(parseFloat(form.amount)) && (
              <p className="mt-1 text-xs text-gray-500">
                Suggested tier from policy:{' '}
                <span className="font-medium text-gray-800">
                  {resolveTierFromAmount(parseFloat(form.amount), selectedVenue.policy)}
                </span>{' '}
                ({formatCurrency(parseFloat(form.amount))})
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payout tier</label>
            <select
              value={form.payout_tier}
              onChange={onTierChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              {TIERS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">Auto-filled from amount; override if needed.</p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Occupation {tierPolicy?.occupationRequired ? '*' : '(optional)'}
          </label>
          <input
            type="text"
            value={form.occupation}
            onChange={(e) => setForm((p) => ({ ...p, occupation: e.target.value }))}
            placeholder={
              tierPolicy?.occupationRequired
                ? 'Required by venue payout policy for this tier'
                : 'If required by rule, enter or select from profile'
            }
            className="w-full border border-gray-300 rounded-md px-3 py-2"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
            <select
              value={form.payment_method}
              onChange={(e) => setForm((p) => ({ ...p, payment_method: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Game ID</label>
            <input
              type="text"
              value={form.game_id}
              onChange={(e) => setForm((p) => ({ ...p, game_id: e.target.value }))}
              placeholder="Optional"
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea
            value={form.notes}
            onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
            rows={2}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
          />
        </div>

        {/* S4 — Cashier attestation. Text comes from the server-resolved
            active version, which is stamped onto the payout record on
            submit so the assessor can see exactly what wording the
            cashier agreed to at the point of capture. */}
        <div className="rounded-lg border border-psg-teal/30 bg-psg-teal/5 p-4">
          <div className="flex items-start gap-2 mb-2">
            <ShieldCheckIcon className="w-5 h-5 text-psg-teal shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-psg-teal">Cashier attestation</div>
              {cashierAttestation.loading && (
                <div className="text-xs text-gray-500 mt-1">Loading active wording…</div>
              )}
              {cashierAttestation.error && (
                <div className="text-xs text-red-600 mt-1">
                  {cashierAttestation.error}. Payout can still be recorded but no attestation version will be stamped.
                </div>
              )}
              {!cashierAttestation.loading && !cashierAttestation.error && !cashierAttestation.data && (
                <div className="text-xs text-amber-700 mt-1">
                  No active cashier attestation version configured for this tenant. Payout will be recorded without a version stamp. Admins: see{' '}
                  <code className="font-mono">GET /api/attestation-versions?code=cashier_payout_confirm</code>.
                </div>
              )}
              {cashierAttestation.data && (
                <>
                  <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap leading-relaxed">
                    {cashierAttestation.data.body_text}
                  </p>
                  <div className="text-[11px] text-gray-500 mt-2 font-mono">
                    Version <span className="font-semibold">{cashierAttestation.data.id}</span>
                    {cashierAttestation.data.version_label ? ` (${cashierAttestation.data.version_label})` : ''}
                    {' · '}regime {cashierAttestation.data.regime_version}
                  </div>
                </>
              )}
            </div>
          </div>
          {patronAttestation.data && (
            <label className="flex items-start gap-2 mt-3 pt-3 border-t border-psg-teal/20 cursor-pointer">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                className="mt-1 rounded border-gray-300"
              />
              <span className="text-xs text-gray-600 leading-relaxed">
                <b>Patron acknowledgement captured.</b> Check this only if the patron has physically / verbally acknowledged the following:
                <span className="block italic text-gray-500 mt-1">"{patronAttestation.data.body_text}"</span>
                <span className="block text-[11px] font-mono text-gray-400 mt-1">Version {patronAttestation.data.id}</span>
              </span>
            </label>
          )}
        </div>

        <div className="flex justify-end pt-4">
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2 bg-psg-teal text-white rounded-lg hover:bg-psg-teal-600 disabled:opacity-50 font-medium"
          >
            {submitting ? 'Recording…' : 'Record Payout'}
          </button>
        </div>
      </form>
    </div>
  );
}
